import React, { useState, useMemo, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { MapPin, Calendar, Briefcase, Navigation } from 'lucide-react';
import type { Technician } from '@shared/types';
import { format, parseISO, isWithinInterval } from 'date-fns';
import { fr } from 'date-fns/locale';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { geocodeAddress, getFullAddress } from '@shared/utils/geocoding';

// Fix Leaflet default marker icons
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

interface MapPageProps {
    technicians: Technician[];
}

// Coordonnées approximatives des villes (Fallback)
const cityCoordinates: Record<string, [number, number]> = {
    'Saint-Étienne': [45.4397, 4.3872],
    'Lyon': [45.7640, 4.8357],
    'Clermont-Ferrand': [45.7772, 3.0870],
    'Saint-Chamond': [45.4781, 4.5158],
    'Rumilly': [45.8647, 4.2047],
    'Andrézieux': [45.5333, 4.2667],
    'Andrézieux-Bouthéon': [45.5333, 4.2667],
    'Colombier-Saugnieu': [45.7167, 5.1167],
    'Saint-Bonnet-de-Mure': [45.6906, 5.0292],
    'Saint-Bonnet': [45.6906, 5.0292],
};

// Create custom colored markers
const createColoredIcon = (color: string, label: string) => {
    const svgIcon = `
        <svg width="40" height="50" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 0c-8.837 0-16 7.163-16 16 0 12 16 26 16 26s16-14 16-26c0-8.837-7.163-16-16-16z" 
                  fill="${color}" stroke="white" stroke-width="2"/>
            <circle cx="20" cy="16" r="10" fill="white"/>
            <text x="20" y="21" text-anchor="middle" font-size="14" font-weight="bold" fill="${color}">${label}</text>
        </svg>
    `;

    return L.divIcon({
        html: svgIcon,
        className: 'custom-marker',
        iconSize: [40, 50],
        iconAnchor: [20, 50],
        popupAnchor: [0, -50]
    });
};

const markerColors = ['#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#14b8a6'];

// Generate slightly offset coordinates for interventions in the same city or same exact location
const getInterventionCoords = (
    baseCoords: [number, number],
    index: number,
    total: number
): [number, number] => {
    if (total <= 1) return baseCoords;

    // Radius of dispersion (approximately 20-30 meters for close points)
    // 0.0002 degrees is roughly 20 meters
    const radius = 0.0002 * (1 + Math.floor(index / 8));

    // Angle based on index
    const angle = (index % 8) * (Math.PI / 4);

    return [
        baseCoords[0] + radius * Math.cos(angle),
        baseCoords[1] + radius * Math.sin(angle)
    ];
};

export const MapPage: React.FC<MapPageProps> = ({ technicians }) => {
    const [selectedDate, setSelectedDate] = useState('2026-01-06');
    const [displayMode, setDisplayMode] = useState<'city' | 'intervention' | 'technician'>('city');
    const [selectedTechnicianId, setSelectedTechnicianId] = useState<string>('');
    const [geocodedCoords, setGeocodedCoords] = useState<Record<string, { lat: number; lng: number }>>({});

    // Safety ref to prevent infinite loops and repeated requests
    const attemptedGeocodes = useRef<Set<string>>(new Set());

    // Get active assignments for selected date
    const activeAssignments = useMemo(() => {
        const assignments: Array<{ assignment: any; technician: Technician; city: string }> = [];
        const selectedDt = parseISO(selectedDate);

        technicians.forEach(tech => {
            tech.assignments.forEach(assignment => {
                try {
                    const start = parseISO(assignment.startDate);
                    const end = parseISO(assignment.endDate);

                    if (isWithinInterval(selectedDt, { start, end })) {
                        const city = assignment.city || 'Saint-Étienne';
                        assignments.push({ assignment, technician: tech, city });
                    }
                } catch (e) {
                    console.error('Error parsing assignment', assignment.id, e);
                }
            });
        });

        return assignments;
    }, [technicians, selectedDate]);

    // Group by city
    const groupedByCity = useMemo(() => {
        const groups = new Map<string, Array<{ assignment: any; technician: Technician }>>();

        activeAssignments.forEach(({ assignment, technician, city }) => {
            if (!groups.has(city)) {
                groups.set(city, []);
            }
            groups.get(city)!.push({ assignment, technician });
        });

        return groups;
    }, [activeAssignments]);

    // Filter assignments for display based on mode
    const displayedAssignments = useMemo(() => {
        if (displayMode === 'technician' && selectedTechnicianId) {
            return activeAssignments.filter(
                ({ technician }) => technician.id === selectedTechnicianId
            );
        }
        return activeAssignments;
    }, [displayMode, selectedTechnicianId, activeAssignments]);


    // Calculate map center and bounds
    const mapSettings = useMemo(() => {
        if (activeAssignments.length === 0) {
            return {
                center: [45.4397, 4.3872] as [number, number],
                zoom: 9
            };
        }

        const coords = activeAssignments
            .map(a => {
                const precise = geocodedCoords[a.assignment.id] || a.assignment.coordinates;
                if (precise) return [precise.lat, precise.lng] as [number, number];
                return cityCoordinates[a.city];
            })
            .filter(Boolean);

        if (coords.length === 0) {
            return {
                center: [45.4397, 4.3872] as [number, number],
                zoom: 9
            };
        }

        const lats = coords.map(c => c[0]);
        const lngs = coords.map(c => c[1]);

        return {
            center: [
                (Math.min(...lats) + Math.max(...lats)) / 2,
                (Math.min(...lngs) + Math.max(...lngs)) / 2
            ] as [number, number],
            zoom: coords.length === 1 ? 12 : 10
        };
    }, [activeAssignments, geocodedCoords]);

    // Trigger Geocoding Safely
    useEffect(() => {
        activeAssignments.forEach(async ({ assignment }) => {
            // 1. Check if we already have coordinates in state
            if (geocodedCoords[assignment.id] || assignment.coordinates) return;

            // 2. Check if we strictly need to geocode (has address or city)
            if (!assignment.address && !assignment.city) return;

            // 3. CRITICAL: Check if we ALREADY attempted this session to prevent loops
            if (attemptedGeocodes.current.has(assignment.id)) return;

            // Mark as attempted immediately
            attemptedGeocodes.current.add(assignment.id);

            const fullAddress = getFullAddress(assignment);
            try {
                const coords = await geocodeAddress(fullAddress);
                if (coords) {
                    // Update state only if we got results, this triggers re-render but
                    // the 'attemptedGeocodes' ref check above prevents infinite loop
                    setGeocodedCoords(prev => ({
                        ...prev,
                        [assignment.id]: coords
                    }));
                }
            } catch (error) {
                console.warn(`Geocoding failed for ${assignment.id}`, error);
            }
        });
    }, [activeAssignments, geocodedCoords]);

    // Get Google Maps URL for all cities/locations
    const getGoogleMapsUrl = () => {
        const locations = activeAssignments.map(a => {
            const precise = geocodedCoords[a.assignment.id] || a.assignment.coordinates;
            if (precise) return `${precise.lat},${precise.lng}`;
            const cityCoord = cityCoordinates[a.city];
            if (cityCoord) return `${cityCoord[0]},${cityCoord[1]}`;
            return '';
        }).filter(Boolean);

        // Deduplicate
        const uniqueLocations = Array.from(new Set(locations));

        if (uniqueLocations.length === 0) return '#';

        return `https://www.google.com/maps/dir/${uniqueLocations.join('/')}`;
    };

    return (
        <div className="h-full flex flex-col space-y-4">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-gray-100 mb-2 flex items-center gap-3">
                    <MapPin className="text-primary" size={32} />
                    Carte des Chantiers
                </h1>
                <p className="text-gray-400">Vue géographique des interventions du jour</p>
            </div>

            {/* Date Selector and Mode Toggle */}
            <div className="glass-panel p-4">
                <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                        <Calendar size={18} className="text-gray-400" />
                        <label className="text-sm font-medium text-gray-300">Date :</label>
                    </div>
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="glass-input"
                    />

                    {/* Display Mode Toggle */}
                    <div className="flex items-center gap-2 ml-4">
                        <label className="text-sm font-medium text-gray-300">Affichage :</label>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setDisplayMode('city')}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${displayMode === 'city'
                                    ? 'bg-primary text-white'
                                    : 'glass-button'
                                    }`}
                            >
                                Par Ville
                            </button>
                            <button
                                onClick={() => setDisplayMode('intervention')}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${displayMode === 'intervention'
                                    ? 'bg-primary text-white'
                                    : 'glass-button'
                                    }`}
                            >
                                Par Intervention
                            </button>
                            <button
                                onClick={() => {
                                    setDisplayMode('technician');
                                    if (!selectedTechnicianId && technicians.length > 0) {
                                        setSelectedTechnicianId(technicians[0].id);
                                    }
                                }}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${displayMode === 'technician'
                                    ? 'bg-primary text-white'
                                    : 'glass-button'
                                    }`}
                            >
                                Par Technicien
                            </button>
                        </div>
                    </div>

                    {/* Technician Selector - Only shown in technician mode */}
                    {displayMode === 'technician' && (
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-300">Technicien :</label>
                            <select
                                value={selectedTechnicianId}
                                onChange={(e) => setSelectedTechnicianId(e.target.value)}
                                className="glass-input"
                            >
                                {technicians.filter(t => !t.archived).map(tech => (
                                    <option key={tech.id} value={tech.id}>
                                        {tech.name} - {tech.service}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="flex items-center gap-4 ml-auto">
                        <div className="flex items-center gap-2">
                            <Briefcase size={16} className="text-accent" />
                            <span className="text-sm text-gray-400">
                                {displayMode === 'technician' ? displayedAssignments.length : activeAssignments.length} chantier(s)
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <MapPin size={16} className="text-primary" />
                            <span className="text-sm text-gray-400">
                                {displayMode === 'city' && `${groupedByCity.size} ville(s)`}
                                {displayMode === 'intervention' && `${activeAssignments.length} intervention(s)`}
                                {displayMode === 'technician' && selectedTechnicianId && (() => {
                                    const tech = technicians.find(t => t.id === selectedTechnicianId);
                                    return `${displayedAssignments.length} intervention(s) - ${tech?.name || ''}`;
                                })()}
                            </span>
                        </div>
                        {activeAssignments.length > 0 && (
                            <a
                                href={getGoogleMapsUrl()}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="glass-button bg-primary/20 border-primary/50 text-primary flex items-center gap-2"
                            >
                                <Navigation size={16} />
                                Itinéraire Google Maps
                            </a>
                        )}
                    </div>
                </div>
            </div>

            {/* Map Container */}
            <div className="glass-panel p-2 rounded-2xl overflow-hidden" style={{ height: '600px' }}>
                {activeAssignments.length === 0 ? (
                    <div className="h-full flex items-center justify-center">
                        <div className="text-center">
                            <MapPin size={64} className="mx-auto text-gray-600 mb-4" />
                            <p className="text-gray-400 text-lg mb-2">Aucun chantier actif à cette date</p>
                            <p className="text-sm text-gray-500">Sélectionnez une autre date pour voir les interventions</p>
                        </div>
                    </div>
                ) : (
                    <div className="h-full w-full rounded-xl overflow-hidden">
                        <MapContainer
                            center={mapSettings.center}
                            zoom={mapSettings.zoom}
                            style={{ height: '100%', width: '100%' }}
                            scrollWheelZoom={true}
                        >
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />


                            {/* Markers - Mode: City */}
                            {displayMode === 'city' && Array.from(groupedByCity.entries()).map(([city, items], idx) => {
                                const coord = cityCoordinates[city];
                                if (!coord) return null;

                                const letter = String.fromCharCode(65 + idx);
                                const color = markerColors[idx % markerColors.length];

                                return (
                                    <Marker
                                        key={city}
                                        position={coord}
                                        icon={createColoredIcon(color, letter)}
                                    >
                                        <Popup maxWidth={350}>
                                            <div className="p-2">
                                                <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                                                    <MapPin size={16} />
                                                    {city}
                                                </h3>
                                                <div className="space-y-3 max-h-64 overflow-y-auto">
                                                    {items.map(({ assignment, technician }, idx) => (
                                                        <div key={idx} className="border-b pb-2 last:border-0">
                                                            <div className="font-semibold text-gray-900">
                                                                {assignment.clientName}
                                                            </div>
                                                            <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                                                                <div>
                                                                    📅 {format(parseISO(assignment.startDate), 'dd MMM', { locale: fr })} →
                                                                    {format(parseISO(assignment.endDate), 'dd MMM yyyy', { locale: fr })}
                                                                </div>
                                                                {(assignment.startTime || assignment.endTime) && (
                                                                    <div>
                                                                        🕐 {assignment.startTime || '--:--'} - {assignment.endTime || '--:--'}
                                                                    </div>
                                                                )}
                                                                {assignment.address && (
                                                                    <div>📍 {assignment.address}</div>
                                                                )}
                                                                {assignment.task && (
                                                                    <div className="text-gray-700 mt-1">💼 {assignment.task}</div>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-2 mt-2">
                                                                <div
                                                                    className="w-3 h-3 rounded-full"
                                                                    style={{ backgroundColor: technician.color }}
                                                                />
                                                                <span className="text-xs font-medium" style={{ color: technician.color }}>
                                                                    {technician.name} - {technician.service}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </Popup>
                                    </Marker>
                                );
                            })}

                            {/* Markers - Mode: Intervention */}
                            {displayMode === 'intervention' && (() => {
                                // Determine coordinates for each assignment
                                const assignmentsWithCoords = activeAssignments.map((item, globalIdx) => {
                                    const { assignment, city } = item;
                                    // Try geocoded coords first, then manual coords
                                    const precise = geocodedCoords[assignment.id] || assignment.coordinates;

                                    if (precise) {
                                        return { ...item, coord: [precise.lat, precise.lng] as [number, number], index: globalIdx, isPrecise: true };
                                    }

                                    // Fallback to city
                                    return { ...item, coord: cityCoordinates[city], index: globalIdx, isPrecise: false };
                                }).filter(item => item.coord);

                                // Group by location to handle overlaps
                                const locationGroups = new Map<string, typeof assignmentsWithCoords>();
                                assignmentsWithCoords.forEach(item => {
                                    const key = `${item.coord[0].toFixed(5)},${item.coord[1].toFixed(5)}`;
                                    if (!locationGroups.has(key)) {
                                        locationGroups.set(key, []);
                                    }
                                    locationGroups.get(key)!.push(item);
                                });

                                return assignmentsWithCoords.map((item) => {
                                    const { assignment, technician, coord, index } = item;
                                    const key = `${coord[0].toFixed(5)},${coord[1].toFixed(5)}`;
                                    const group = locationGroups.get(key)!;

                                    // If multiple markers at same location, offset them
                                    let finalCoord = coord;
                                    if (group.length > 1) {
                                        const localIdx = group.findIndex(g => g.index === index);
                                        finalCoord = getInterventionCoords(coord, localIdx, group.length);
                                    }

                                    const markerLabel = String(index + 1);

                                    return (
                                        <Marker
                                            key={`intervention-${index}`}
                                            position={finalCoord}
                                            icon={createColoredIcon(technician.color, markerLabel)}
                                        >
                                            <Popup maxWidth={350}>
                                                <div className="p-2">
                                                    <h3 className="font-bold text-lg mb-2 text-gray-900">
                                                        {assignment.clientName}
                                                    </h3>
                                                    <div className="space-y-2 text-sm">
                                                        <div className="flex items-center gap-2 text-gray-700">
                                                            <MapPin size={14} />
                                                            <span className="font-medium">{assignment.address || item.city}</span>
                                                        </div>
                                                        <div className="text-xs text-gray-600 space-y-1">
                                                            <div>
                                                                📅 {format(parseISO(assignment.startDate), 'dd MMM', { locale: fr })} →
                                                                {format(parseISO(assignment.endDate), 'dd MMM yyyy', { locale: fr })}
                                                            </div>
                                                            {(assignment.startTime || assignment.endTime) && (
                                                                <div>
                                                                    🕐 {assignment.startTime || '--:--'} - {assignment.endTime || '--:--'}
                                                                </div>
                                                            )}
                                                            {assignment.task && (
                                                                <div className="text-gray-700 mt-1">💼 {assignment.task}</div>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 pt-2 border-t">
                                                            <div
                                                                className="w-4 h-4 rounded-full"
                                                                style={{ backgroundColor: technician.color }}
                                                            />
                                                            <span className="text-sm font-medium" style={{ color: technician.color }}>
                                                                {technician.name}
                                                            </span>
                                                            <span className="text-xs text-gray-500">({technician.service})</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Popup>
                                        </Marker>
                                    );
                                });
                            })()}

                            {/* Markers - Mode: Technician */}
                            {displayMode === 'technician' && selectedTechnicianId && (() => {
                                if (displayedAssignments.length === 0) return null;

                                const selectedTech = technicians.find(t => t.id === selectedTechnicianId);
                                if (!selectedTech) return null;

                                const assignmentsWithCoords = displayedAssignments.map((item, globalIdx) => {
                                    const { assignment, city } = item;
                                    const precise = geocodedCoords[assignment.id] || assignment.coordinates;

                                    if (precise) {
                                        return { ...item, coord: [precise.lat, precise.lng] as [number, number], index: globalIdx, isPrecise: true };
                                    }
                                    return { ...item, coord: cityCoordinates[city], index: globalIdx, isPrecise: false };
                                }).filter(item => item.coord);

                                const locationGroups = new Map<string, typeof assignmentsWithCoords>();
                                assignmentsWithCoords.forEach(item => {
                                    const key = `${item.coord[0].toFixed(5)},${item.coord[1].toFixed(5)}`;
                                    if (!locationGroups.has(key)) {
                                        locationGroups.set(key, []);
                                    }
                                    locationGroups.get(key)!.push(item);
                                });

                                return assignmentsWithCoords.map((item) => {
                                    const { assignment, coord, index } = item;
                                    const key = `${coord[0].toFixed(5)},${coord[1].toFixed(5)}`;
                                    const group = locationGroups.get(key)!;

                                    let finalCoord = coord;
                                    if (group.length > 1) {
                                        const localIdx = group.findIndex(g => g.index === index);
                                        finalCoord = getInterventionCoords(coord, localIdx, group.length);
                                    }

                                    const markerLabel = String(index + 1);

                                    return (
                                        <Marker
                                            key={`tech-intervention-${index}`}
                                            position={finalCoord}
                                            icon={createColoredIcon(selectedTech.color, markerLabel)}
                                        >
                                            <Popup maxWidth={350}>
                                                <div className="p-2">
                                                    <h3 className="font-bold text-lg mb-2 text-gray-900">
                                                        {assignment.clientName}
                                                    </h3>
                                                    <div className="space-y-2 text-sm">
                                                        <div className="flex items-center gap-2 text-gray-700">
                                                            <MapPin size={14} />
                                                            <span className="font-medium">{assignment.address || item.city}</span>
                                                        </div>
                                                        <div className="text-xs text-gray-600 space-y-1">
                                                            <div>
                                                                📅 {format(parseISO(assignment.startDate), 'dd MMM', { locale: fr })} →
                                                                {format(parseISO(assignment.endDate), 'dd MMM yyyy', { locale: fr })}
                                                            </div>
                                                            {(assignment.startTime || assignment.endTime) && (
                                                                <div>
                                                                    🕐 {assignment.startTime || '--:--'} - {assignment.endTime || '--:--'}
                                                                </div>
                                                            )}
                                                            {assignment.task && (
                                                                <div className="text-gray-700 mt-1">💼 {assignment.task}</div>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 pt-2 border-t">
                                                            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: selectedTech.color }} />
                                                            <span className="text-sm font-medium" style={{ color: selectedTech.color }}>
                                                                {selectedTech.name}
                                                            </span>
                                                            <span className="text-xs text-gray-500">({selectedTech.service})</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Popup>
                                        </Marker>
                                    );
                                });
                            })()}

                        </MapContainer>
                    </div>
                )}
            </div>

            {/* Legend */}
            {activeAssignments.length > 0 && (
                <div className="glass-panel p-4">
                    <h3 className="text-sm font-semibold text-gray-300 mb-3">
                        📅 {format(parseISO(selectedDate), 'EEEE dd MMMM yyyy', { locale: fr })}
                    </h3>
                    <div className="flex flex-wrap gap-3">
                        {technicians
                            .filter(tech => activeAssignments.some(a => a.technician.id === tech.id))
                            .map(tech => {
                                const count = activeAssignments.filter(a => a.technician.id === tech.id).length;
                                return (
                                    <div
                                        key={tech.id}
                                        className="flex items-center gap-2 px-3 py-2 rounded-lg"
                                        style={{ backgroundColor: `${tech.color}20`, border: `1px solid ${tech.color}40` }}
                                    >
                                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: tech.color }} />
                                        <span className="text-sm font-medium" style={{ color: tech.color }}>
                                            {tech.name}
                                        </span>
                                        <span className="text-xs text-gray-400">({count} chantier{count > 1 ? 's' : ''})</span>
                                    </div>
                                );
                            })}
                    </div>
                </div>
            )}

            <style>{`
                .custom-marker {
                    background: none;
                    border: none;
                }
                .leaflet-popup-content-wrapper {
                    border-radius: 12px;
                }
                .leaflet-popup-content {
                    margin: 0;
                }
            `}</style>
        </div>
    );
};
